import React from 'react';
import { GetStaticProps } from 'next';
import { BlogLayout } from '../../components/blog/BlogLayout';
import { BlogSEO } from '../../components/blog/BlogSEO';
import { BlogCard } from '../../components/blog/BlogCard';
import { getAllPosts, getAllTags, BlogPost } from '../../lib/blog';

interface BlogIndexProps {
  posts: BlogPost[];
  tags: string[];
}

const BlogIndex: React.FC<BlogIndexProps> = ({ posts, tags }) => {
  return (
    <>
      <BlogSEO isBlogHome posts={posts} />
      <div className="bg-[#0F1624] text-white w-full">
        <BlogLayout>
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              SoulMinter Blog
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Learn how to create Solana tokens, understand SPL tokens, and master token creation 
              with our comprehensive guides and tutorials.
            </p>
          </div>

          {/* Featured Post */}
          {posts.length > 0 && (
            <div className="mb-16">
              <h2 className="text-2xl font-bold text-white mb-6">Featured Article</h2>
              <div className="bg-gradient-to-r from-[#9945FF] to-[#00C2FF] p-1 rounded-lg">
                <div className="bg-[#0F1624] rounded-lg p-1">
                  <BlogCard post={posts[0]} />
                </div>
              </div>
            </div>
          )}

          {/* All Posts */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-6">All Articles</h2>
            {posts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400 text-lg">No blog posts available yet.</p>
                <p className="text-gray-500 mt-2">Check back soon for new content!</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.map((post) => (
                  <BlogCard key={post.slug} post={post} />
                ))}
              </div>
            )}
          </div>

          {/* Tags Section */}
          {tags.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">Browse by Topic</h2>
              <div className="flex flex-wrap gap-3">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-4 py-2 bg-[#192038] text-white rounded-full hover:bg-[#9945FF] transition-colors cursor-pointer"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Call to Action */}
          <div className="text-center p-8 bg-[#192038] rounded-lg">
            <h2 className="text-2xl font-bold text-white mb-4">
              Ready to Create Your First Token?
            </h2>
            <p className="text-gray-300 mb-6">
              Put your knowledge into practice with SoulMinter&apos;s no-code platform.
            </p>
            <a
              href="https://soulminter.io/create"
              className="inline-block px-8 py-3 bg-gradient-to-r from-[#9945FF] to-[#00C2FF] text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              Start Creating
            </a>
          </div>
        </BlogLayout>
      </div>
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const posts = getAllPosts();
  const tags = getAllTags();

  return {
    props: {
      posts,
      tags,
    },
  };
};

export default BlogIndex;
